/*
 * Notify me when available.
 *
 * Two modes, picked by the presence of the `custom.oos_klaviyo_list_id`
 * product metafield:
 *
 *   - no metafield  -> creates a Klaviyo back in stock subscription for the
 *     selected variant. This logs the `Subscribed to Back in Stock` metric,
 *     which is what the back in stock flow triggers on.
 *   - metafield set -> subscribes the profile to that list instead. Used for
 *     pre-release products, where the launch email is sent as a campaign to
 *     the list rather than automatically by the back in stock flow.
 *
 * Both endpoints are Klaviyo's client-side APIs, which take the public
 * company id only. Never put a private API key in here.
 */

const KLAVIYO_API_REVISION = '2026-07-15';

class NotifyMeForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.emailInput = this.querySelector('input[type="email"]');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.errorMessage = this.querySelector('.notify-me__error');
    this.formWrapper = this.querySelector('.notify-me__form-wrapper');
    this.successMessage = this.querySelector('.notify-me__success');

    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  // Prefer the value rendered by app_klaviyo.liquid, but fall back to reading
  // it off the Klaviyo app embed's script tag so this keeps working on preview
  // themes, where request.host is a myshopify.com domain.
  get companyId() {
    if (window.KlaviyoCompanyId) return window.KlaviyoCompanyId;

    const script = document.querySelector(
      'script[src*="klaviyo.com/onsite/js"][src*="company_id="]'
    );
    if (!script) return null;

    return new URL(script.src, window.location.href).searchParams.get(
      'company_id'
    );
  }

  get listId() {
    return this.dataset.listId || '';
  }

  // Read the variant off the product form rather than caching it, since
  // product-info.js rewrites that input on every variant change.
  get variantId() {
    const input = document.querySelector(
      `#${this.dataset.productFormId} input[name="id"]`
    );
    return input?.value || this.dataset.variantId;
  }

  onSubmit(event) {
    event.preventDefault();

    const email = this.emailInput.value.trim();
    if (!email || !this.emailInput.checkValidity()) {
      this.showError(window.notifyMeStrings.invalidEmail);
      return;
    }

    const companyId = this.companyId;
    if (!companyId) {
      console.error('[notify-me] No Klaviyo company id found on the page.');
      this.showError(window.notifyMeStrings.error);
      return;
    }

    this.setLoading(true);
    this.hideError();

    const request = this.listId
      ? this.subscribeToList(companyId, email)
      : this.subscribeToBackInStock(companyId, email);

    request
      .then((response) => {
        if (!response.ok) throw new Error(`Klaviyo returned ${response.status}`);
        this.showSuccess();
      })
      .catch((error) => {
        console.error('[notify-me]', error);
        this.showError(window.notifyMeStrings.error);
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  post(companyId, path, payload) {
    return fetch(
      `https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(
        companyId
      )}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: KLAVIYO_API_REVISION,
        },
        body: JSON.stringify(payload),
      }
    );
  }

  subscribeToBackInStock(companyId, email) {
    const variantId = this.variantId;
    if (!variantId) {
      return Promise.reject(new Error('No variant id available'));
    }

    return this.post(companyId, 'back-in-stock-subscriptions', {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              // Klaviyo's Shopify catalog id format.
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    });
  }

  subscribeToList(companyId, email) {
    return this.post(companyId, 'subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.dataset.customSource || 'Notify me',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: { marketing: { consent: 'SUBSCRIBED' } },
                },
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: this.listId } },
        },
      },
    });
  }

  setLoading(loading) {
    this.submitButton.classList.toggle('loading', loading);
    this.submitButton.toggleAttribute('disabled', loading);
  }

  showError(message) {
    this.errorMessage.textContent = message;
    this.errorMessage.hidden = false;
  }

  hideError() {
    this.errorMessage.hidden = true;
  }

  showSuccess() {
    this.formWrapper.hidden = true;
    this.successMessage.hidden = false;
  }
}

customElements.define('notify-me-form', NotifyMeForm);
